import {useMemo} from 'react'
import {type TimelineItem, TimelineItemType} from '@github-ui/dotcom-schema/types/pull_requests/v1/timeline'
import {useInfiniteTimelinePageData} from '../../../page-data/loaders/use-timeline'
import {usePullRequestBasePath} from '../../../stores/selectors'
import type {TimelineActivityGroupKey} from '../../../contexts/DockedPanelContext'
import {TimelineItemList} from './TimelineItemList'
import {useInfiniteScrollLoad} from './use-infinite-scroll-load'
import styles from './TimelinePanel.module.css'

interface TimelineActivityGroupProps {
  label: string
  group: TimelineActivityGroupKey
  since?: string
  expectedCount?: number
}

function groupPredicate(group: TimelineActivityGroupKey): (item: TimelineItem) => boolean {
  switch (group) {
    case 'commits':
      return item => item.additionalInfo?.info?.case === 'pullRequestCommit'
    case 'baseForce':
      return item => {
        const info = item.additionalInfo?.info
        return (
          info?.case === 'issueEvent' &&
          ['head_ref_force_pushed', 'base_ref_force_pushed', 'base_ref_changed'].includes(info.value.eventType)
        )
      }
    case 'approvals':
      return item => {
        const info = item.additionalInfo?.info
        return info?.case === 'pullRequestReview' && info.value.state === 'approved'
      }
    case 'changesRequested':
      return item => {
        const info = item.additionalInfo?.info
        return info?.case === 'pullRequestReview' && info.value.state === 'changes_requested'
      }
    case 'comments':
      return item => {
        const info = item.additionalInfo?.info
        return info?.case === 'pullRequestReview' && info.value.state === 'commented'
      }
    case 'issueComments':
      return item => item.additionalInfo?.info?.case === 'issueComment'
    case 'state':
      return item => {
        const info = item.additionalInfo?.info
        return (
          info?.case === 'issueEvent' &&
          ['closed', 'reopened', 'merged', 'convert_to_draft', 'ready_for_review'].includes(info.value.eventType)
        )
      }
    case 'labels':
      return item => {
        const info = item.additionalInfo?.info
        return info?.case === 'issueEvent' && ['labeled', 'unlabeled'].includes(info.value.eventType)
      }
    case 'reviewRequests':
      return item => {
        const info = item.additionalInfo?.info
        return (
          info?.case === 'issueEvent' && ['review_requested', 'review_request_removed'].includes(info.value.eventType)
        )
      }
    case 'description':
      return item => item.type === TimelineItemType.ISSUE_EDIT
  }
}

export function TimelineActivityGroup({label, group, since, expectedCount}: TimelineActivityGroupProps) {
  const pathName = usePullRequestBasePath()
  const {data, fetchNextPage, hasNextPage, isFetchingNextPage} = useInfiniteTimelinePageData({pathName})

  const allItems = useMemo(() => data.pages.flatMap(page => page.data), [data.pages])
  const predicate = useMemo(() => groupPredicate(group), [group])
  const sinceDate = useMemo(() => (since ? new Date(since) : null), [since])

  const filtered = useMemo(
    () =>
      allItems.filter(item => {
        if (!predicate(item)) return false
        if (sinceDate && item.timestamp) {
          return new Date(item.timestamp) >= sinceDate
        }
        return true
      }),
    [allItems, predicate, sinceDate],
  )

  // Keep loading pages while the group might still have matching events on later pages, but stop
  // once we've gathered the count the sidebar promised — bounds fetching to avoid pulling the whole
  // timeline when counts come from a different endpoint than the timeline data.
  const hasMore = hasNextPage && (expectedCount == null || filtered.length < expectedCount)
  const sentinel = useInfiniteScrollLoad({fetchNextPage, hasMore, isFetchingNextPage})

  return (
    <div role="region" aria-label={label}>
      <h4 className={styles.activityGroupHeading}>{label}</h4>
      <TimelineItemList
        items={filtered}
        clipSidebar={hasMore && isFetchingNextPage ? false : 'end'}
        sentinel={sentinel}
      />
    </div>
  )
}
